import {
    BankAccountBody,
    ConfirmPaymentTokenBody,
    CustomerBody,
    CustomerResponse,
    PaymentTokenBody,
} from "../../sdk/types"
import { UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { APIError } from "../../sdk/api"
import { useAPIClient } from "../../context/client"
import { useEffect } from "react"

const useQueryCustomerId = (options?: { background?: boolean }) => {
    const {
        client,
        state: { customer_id, customerWeakToken },
    } = useAPIClient()

    return useQuery(["getCustomerId", { token: customerWeakToken }], () => client.getCustomerId(), {
        enabled: !!customerWeakToken && !customer_id,
        meta: { background: options?.background, errorToast: true },
    })
}

const useQueryCustomer = (
    queryOptions?: Omit<UseQueryOptions<CustomerResponse, APIError>, "queryKey" | "queryFn" | "initialData">,
    options?: { background?: boolean }
) => {
    const {
        client,
        state: { customer_id },
    } = useAPIClient()

    const query = useQuery<CustomerResponse, APIError>(
        ["getCustomer", { id: customer_id }],
        () => client.getCustomer(customer_id as string),
        {
            enabled: !!customer_id,
            ...queryOptions,
            meta: { background: options?.background, errorToast: true },
        }
    )

    useEffect(() => {
        const error = query.error as APIError
        if (!error || error.status !== 403) return

        client.state.customer_id = undefined
    }, [query.error])

    return query
}

const useMutationCustomer = (token?: "customerWeakToken") => {
    const queryClient = useQueryClient()
    const {
        client,
        state: { customer_id },
    } = useAPIClient()

    return useMutation((body: CustomerBody) => client.updateCustomer(customer_id as string, body, token), {
        onSuccess: data => queryClient.setQueryData(["getCustomer", { id: customer_id }], data),
    })
}

const usePostBankAccount = () => {
    const queryClient = useQueryClient()
    const {
        client,
        state: { customer_id },
    } = useAPIClient()

    return useMutation((body: BankAccountBody) => client.createBankAccount(customer_id as string, body), {
        onSuccess: () => queryClient.invalidateQueries(["getCustomer", { id: customer_id }]),
    })
}

const useMutationBankAccount = (id: string) => {
    const queryClient = useQueryClient()
    const {
        client,
        state: { customer_id },
    } = useAPIClient()

    return useMutation((body: BankAccountBody) => client.updateBankAccount(customer_id as string, id, body), {
        onSuccess: () => queryClient.invalidateQueries(["getCustomer", { id: customer_id }]),
    })
}

const useRemoveBankAccount = () => {
    const queryClient = useQueryClient()
    const {
        client,
        state: { customer_id },
    } = useAPIClient()

    return useMutation((id: string) => client.removeBankAccount(customer_id as string, id), {
        onSuccess: () => queryClient.invalidateQueries(["getCustomer", { id: customer_id }]),
    })
}

const useRequestPaymentToken = () => {
    const {
        client,
        state: { customer_id },
    } = useAPIClient()

    return useMutation((body: PaymentTokenBody) => client.requestPaymentToken(customer_id as string, body))
}

const useConfirmPaymentToken = () => {
    const {
        client,
        state: { customer_id },
    } = useAPIClient()

    return useMutation((body: ConfirmPaymentTokenBody) => client.confirmPaymentToken(customer_id as string, body))
}

export {
    useQueryCustomerId,
    useQueryCustomer,
    useMutationCustomer,
    usePostBankAccount,
    useMutationBankAccount,
    useRemoveBankAccount,
    useRequestPaymentToken,
    useConfirmPaymentToken,
}
